import { wallOpeningPlanExtents } from "building-common/openings/openingQuery";
import type { SolidWall } from "building-types/walls/wallTypes";
import { ensureDefined } from "common/utils/assertOfType";
import { closestParamLine2 } from "geom/closestPoint";
import { DIST_EPSILON } from "geom/core";
import { intersectLineSegment2Circle2 } from "geom/intersect";
import { line2Eval } from "geom/line2";
import {
  polyLine2EdgeCount,
  polyLine2EdgeEnd,
  polyLine2EdgeLength,
  polyLine2EdgeStart,
  polyLine2EdgeTangent,
} from "geom/polyLine2";
import { polyLine2New } from "geom/polyLine2Types";
import { vec2Dist, vec2Dot } from "geom/vec2";
import { makeDoorOperationInfo } from "../../doors/doorOperationInfo";
import { subdivideWallIntervalsWithInterval } from "./wallInterval";
import { getCenterPlanOutlineVertexAlongTraversal, vec2SequenceAngleCW } from "./wallIntervalLoopUtils";
import {
  getFirstPlanOutlineVertexAlongTraversal,
  getLastPlanOutlineVertexAlongTraversal,
  makeWallInterval,
} from "./wallIntervalUtils";
import type { WallInterval } from "./wallTraverseTypes";
import { WallIntervalEntityType } from "./wallTraverseTypes";

export interface DoorSwingIntervalInfo {
  readonly edgeIndex: number;
  readonly param: number;
  readonly hingeX: number;
  readonly hingeY: number;
  readonly doorCenterX: number; // center of door opening
  readonly doorCenterY: number; // center of door opening
  readonly swingsRight: boolean; // which way does the latch side point after opening it facing the door from inside the room
  readonly distance: number;
  readonly doorSwingStopsAtCorners: boolean;
}

/**
 * It's often the case that a door swing will intersect multiple wall intervals. This function
 * subdivides multiple wall intervals as needed to account for the door swing.
 */
export function subdivideWallIntervalLoopWithDoorSwings(
  loop: WallInterval[][],
  collinearJoinedVertices: number[],
  doorSwings: DoorSwingIntervalInfo[],
) {
  const pl = polyLine2New(collinearJoinedVertices);
  const edgeCount = polyLine2EdgeCount(pl);

  for (const {
    edgeIndex,
    param,
    hingeX,
    hingeY,
    doorCenterX,
    doorCenterY,
    distance,
    swingsRight,
    doorSwingStopsAtCorners,
  } of doorSwings) {
    const [tx, ty] = polyLine2EdgeTangent(pl, edgeIndex, swingsRight);
    const edgeLength = polyLine2EdgeLength(pl, edgeIndex);
    const [x0, y0] = swingsRight ? polyLine2EdgeEnd(pl, edgeIndex) : polyLine2EdgeStart(pl, edgeIndex);

    // Useful for debugging
    // debugPoint({
    //   shape: "x",
    //   size: 4,
    //   x: x,
    //   y: y,
    //   mat: DebugColor.DarkGreen,
    // });

    for (let index = 0; index < edgeCount; index++) {
      const i = swingsRight ? (edgeIndex - index + edgeCount) % edgeCount : (edgeIndex + index) % edgeCount;

      const [x1, y1] = swingsRight ? polyLine2EdgeEnd(pl, i) : polyLine2EdgeStart(pl, i);

      if (index !== 0 && (vec2Dist(x1, y1, hingeX, hingeY) > distance || doorSwingStopsAtCorners)) {
        break;
      }

      const [x2, y2] = swingsRight ? polyLine2EdgeStart(pl, i) : polyLine2EdgeEnd(pl, i);

      if (
        index !== 0 &&
        vec2Dist(x2, y2, hingeX, hingeY) > distance &&
        (swingsRight
          ? vec2SequenceAngleCW(x0, y0, x1, y1, x2, y2) < -Math.PI / 4
          : vec2SequenceAngleCW(x0, y0, x1, y1, x2, y2) > Math.PI / 4)
      ) {
        break;
      }

      let ints = intersectLineSegment2Circle2(x1, y1, x2, y2, hingeX, hingeY, distance);

      ints = ints.filter((int) => vec2Dot(tx, ty, int[0] - doorCenterX, int[1] - doorCenterY) > DIST_EPSILON);
      if (ints.length > 1) {
        ints.sort(
          (a, b) =>
            closestParamLine2(x1, y1, x2, y2, b[0], b[1], true) - closestParamLine2(x1, y1, x2, y2, a[0], a[1], true),
        );
      }

      // if there are no intersections, then this edge is captured by the door swing
      if (ints.length === 0) {
        let edgeIntervals = loop[i];

        // if  first interval, then subdivide the first interval starting at the spring point
        if (index === 0) {
          edgeIntervals = subdivideWallIntervalsWithInterval(
            edgeIntervals,
            swingsRight
              ? makeWallInterval(0, param, WallIntervalEntityType.DOOR_SWING)
              : makeWallInterval(param, edgeLength, WallIntervalEntityType.DOOR_SWING),
          );
        } else {
          // fully captures the edge
          edgeIntervals = subdivideWallIntervalsWithInterval(
            edgeIntervals,
            makeWallInterval(0, edgeLength, WallIntervalEntityType.DOOR_SWING),
          );
        }
        loop[i] = edgeIntervals;
        continue;
      }

      const [intx, inty] = ints[0];
      const strikeParam = swingsRight
        ? closestParamLine2(x2, y2, x1, y1, intx, inty, true)
        : closestParamLine2(x1, y1, x2, y2, intx, inty, true);

      let edgeIntervals = loop[i];

      // if  first interval, then subdivide the first interval starting at the spring point
      if (index === 0) {
        edgeIntervals = subdivideWallIntervalsWithInterval(
          edgeIntervals,
          swingsRight
            ? makeWallInterval(strikeParam, param, WallIntervalEntityType.DOOR_SWING)
            : makeWallInterval(param, strikeParam, WallIntervalEntityType.DOOR_SWING),
        );
      } else {
        // fully captures the edge up to the strike param
        edgeIntervals = subdivideWallIntervalsWithInterval(
          edgeIntervals,
          swingsRight
            ? makeWallInterval(strikeParam, polyLine2EdgeLength(pl, i), WallIntervalEntityType.DOOR_SWING)
            : makeWallInterval(0, strikeParam, WallIntervalEntityType.DOOR_SWING),
        );
      }
      loop[i] = edgeIntervals;

      // Useful for debugging
      // debugPolyLine({
      //   pline: polyLine2New([x, y, intx, inty]),
      //   mat: DebugColor.DarkGreen,
      // });
      // debugPoint({
      //   shape: "x",
      //   size: 2,
      //   x: intx,
      //   y: inty,
      //   mat: DebugColor.DarkGreen,
      // });
    }
  }
}

export function getDoorSwings(
  leftIsSide: boolean,
  wall: SolidWall,
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  doorSwings: DoorSwingIntervalInfo[],
  index: string,
) {
  const visitedEntityIds: { [entityId: string]: boolean } = {};
  const { leftSide, rightSide } = wall;
  const wallSide = leftIsSide ? leftSide : rightSide;
  const wallOppSide = leftIsSide ? rightSide : leftSide;

  for (const door of wallSide.doors) {
    visitedEntityIds[door.id] = true;

    const doorOperationInfo = makeDoorOperationInfo(door);
    if (doorOperationInfo) {
      const [rx, ry] = getFirstPlanOutlineVertexAlongTraversal({
        planOutline: wallOpeningPlanExtents(ensureDefined(door.opening)),
        clockwise: false,
        frontEdge: true,
      });

      const r = closestParamLine2(x1, y1, x2, y2, rx, ry, true);

      const [lx, ly] = getLastPlanOutlineVertexAlongTraversal({
        planOutline: wallOpeningPlanExtents(ensureDefined(door.opening)),
        clockwise: false,
        frontEdge: true,
      });

      const l = closestParamLine2(x1, y1, x2, y2, lx, ly, true);

      const [doorFrontCenterX, doorFrontCenterY] = getCenterPlanOutlineVertexAlongTraversal({
        planOutline: wallOpeningPlanExtents(ensureDefined(door.opening)),
        frontEdge: true,
      });
      const { frontRightSwing, frontLeftSwing, doorSwingStopsAtCorners } = doorOperationInfo;
      if (frontRightSwing) {
        const [hingeX, hingeY] = line2Eval(x1, y1, x2, y2, r, false);
        doorSwings.push({
          edgeIndex: +index,
          param: r,
          hingeX,
          hingeY,
          doorCenterX: doorFrontCenterX,
          doorCenterY: doorFrontCenterY,
          swingsRight: true,
          distance: frontRightSwing,
          doorSwingStopsAtCorners,
        });
      }

      if (frontLeftSwing) {
        const [hingeX, hingeY] = line2Eval(x1, y1, x2, y2, l, false);
        doorSwings.push({
          edgeIndex: +index,
          param: l,
          hingeX,
          hingeY,
          doorCenterX: doorFrontCenterX,
          doorCenterY: doorFrontCenterY,
          swingsRight: false,
          distance: frontLeftSwing,
          doorSwingStopsAtCorners,
        });
      }
    }
  }

  for (const door of wallOppSide.doors) {
    //ignore door if it was already visited
    if (visitedEntityIds[door.id]) {
      continue;
    }

    const doorOperationInfo = makeDoorOperationInfo(door);
    if (doorOperationInfo) {
      const [rx, ry] = getFirstPlanOutlineVertexAlongTraversal({
        planOutline: wallOpeningPlanExtents(ensureDefined(door.opening)),
        clockwise: false,
        frontEdge: false,
      });

      const r = closestParamLine2(x1, y1, x2, y2, rx, ry, true);

      const [lx, ly] = getLastPlanOutlineVertexAlongTraversal({
        planOutline: wallOpeningPlanExtents(ensureDefined(door.opening)),
        clockwise: false,
        frontEdge: false,
      });

      const l = closestParamLine2(x1, y1, x2, y2, lx, ly, true);

      const [doorBackCenterX, doorBackCenterY] = getCenterPlanOutlineVertexAlongTraversal({
        planOutline: wallOpeningPlanExtents(ensureDefined(door.opening)),
        frontEdge: false,
      });

      const { backRightSwing, backLeftSwing, doorSwingStopsAtCorners } = doorOperationInfo;
      if (backRightSwing) {
        const [hingeX, hingeY] = line2Eval(x1, y1, x2, y2, r, false);
        doorSwings.push({
          edgeIndex: +index,
          param: r,
          hingeX,
          hingeY,
          doorCenterX: doorBackCenterX,
          doorCenterY: doorBackCenterY,
          swingsRight: true,
          distance: backRightSwing,
          doorSwingStopsAtCorners,
        });
      }

      if (backLeftSwing) {
        const [hingeX, hingeY] = line2Eval(x1, y1, x2, y2, l, false);
        doorSwings.push({
          edgeIndex: +index,
          param: l,
          hingeX,
          hingeY,
          doorCenterX: doorBackCenterX,
          doorCenterY: doorBackCenterY,
          swingsRight: false,
          distance: backLeftSwing,
          doorSwingStopsAtCorners,
        });
      }
    }
  }
}
